<template>
  <div class="client-communication-wrapper">
    <!-- Loading Component for both save operations and assignment data loading -->
    <IsLoadingComponent v-if="showLoading || isLoadingAssignmentData || isLoadingPropertyContacts || isLoadingDataRequest " />

    <!-- Form Content (disabled while loading) -->
    <div :class="{ 'form-disabled': showLoading || isLoadingAssignmentData || isLoadingPropertyContacts || isLoadingDataRequest }">
      <!-- Selected Assignment and Email Section -->
      <SelectedEmailAndAssignment 
        :emailData="emailData"
        :assignment="selectedAssignment"
        class="mb-4"
        @assignmentChanged="handleAssignmentChanged"
      />

      <!-- Reply to Selected Email Checkbox -->
      <div v-if="hasEmailForReply" class="form-field reply-checkbox-section">
        <div class="reply-checkbox-container">
          <v-checkbox
            v-model="formData.replyToSelectedEmail"
            color="#003f2d"
            hide-details
            class="reply-checkbox"
          />
          <label class="field-label checkbox-main-label">Reply to Selected Email</label>
        </div>
        <div class="checkbox-description">
          Replying to the selected email will keep the email's subject and recipient list
        </div>
      </div>

      <!-- Email Template Selection -->
      <!-- <div class="template-selection form-field">
        <label class="field-label">Select Email Template</label>
        <v-autocomplete
          v-model="searchableTemplate"
          :items="emailTemplateOptions"
          placeholder="Search or select email template"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          append-inner-icon="mdi-magnify"
          :append-icon="null"
          class="no-dropdown-icon"
          autocomplete="new-password"
          spellcheck="false"
          :input-props="{ autocomplete: 'new-password', 'data-form-type': 'other' }"
          :menu-props="{ closeOnContentClick: true, maxWidth: '100%' }"
          @update:search="handleSearch"
        >
          <template v-slot:no-data>
            <v-list-item class="no-data-item">
              <v-list-item-title>No data available</v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div> -->

    <!-- Intro Paragraph Selection -->
    <IntroParagraphSelector
      v-model="formData.selectedIntroParagraph"
      label="Intro Paragraph*"
      dialogTitle="Custom Intro"
      dialogDescription="Create a custom intro paragraph to add to the Property Contact Template."
      nameLabel="Intro Name*"
      contentLabel="Intro*"
      @paragraph-changed="onIntroParagraphChanged"
      @error="handleIntroParagraphError"
      @success="handleIntroParagraphSuccess"
      class="form-field"
    />

      <!-- Data Request Field - NEW COMPONENT -->
      <DataRequestSelector
        v-model="formData.dataRequest"
        :systemOptions="propertyTypeOptionsFiltered"
        @request-changed="onDataRequestChanged"
        @error="handleDataRequestError"
        @success="handleDataRequestSuccess"
        class="form-field"
      />

    <!-- Client Contact Field -->
    <div class="form-field">
      <label class="field-label">Client Contact</label>
      <div class="client-contact">
        <div class="contact-name">{{ getClientContactName() }}</div>
        <div class="contact-email">{{ getClientContactEmail() }}</div>
      </div>
    </div>

    <!-- Property Contact Fields -->
    <div class="form-field">
      <label class="field-label">Property Contact*</label>
      <div v-if="isLoadingAssignmentData" class="loading-indicator">
        <v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
        <span class="loading-text">Loading contact details...</span>
      </div>
      <div v-else>
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="formData.propertyContactFirstName"
              placeholder="First Name"
              variant="outlined"
              density="compact"
              hide-details
              autocomplete="new-password"
              spellcheck="false"
              :input-props="{ autocomplete: 'new-password', 'data-form-type': 'other' }"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="formData.propertyContactLastName"
              placeholder="Last Name (Optional)"
              variant="outlined"
              density="compact"
              hide-details
              autocomplete="new-password"
              spellcheck="false"
              :input-props="{ autocomplete: 'new-password', 'data-form-type': 'other' }"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-text-field
          v-model="formData.propertyContactEmail"
          placeholder="Email Address"
          variant="outlined"
          density="compact"
          type="email"
          hide-details
          class="mt-2"
          autocomplete="new-password"
          spellcheck="false"
          :input-props="{ autocomplete: 'new-password', 'data-form-type': 'other' }"
          @blur="validateEmail(formData.propertyContactEmail)"
          @input="validateEmail(formData.propertyContactEmail)"
          :class="{ 'field-error': formErrors.propertyContactEmail }"
        ></v-text-field>
        <!-- Email Error Message -->
        <div v-if="formErrors.propertyContactEmail" class="error-message mt-1">
          {{ formErrors.propertyContactEmail }}
        </div>
      </div>
    </div>

    <!-- Proposed Inspection Date and Time -->
    <div class="form-field">
      <label class="field-label section-header">Proposed Inspection Date</label>
      <div class="date-time-container">
        <div class="date-wrapper">
          <DateComponent
            ref="dateComponent"
            :value="inputDate"
            label="Select Date"
            :required="false"
            field-id="proposedInspectionDate"
            @input="onDateInput"
            compName="ClientCommunication"
          />
        </div>
        <div class="at-label-wrapper">
          <label class="time-label-inline">At</label>
        </div>
        <div class="time-wrapper">
          <TimePickerComponent
            v-model="formData.proposedInspectionTime"
            :disabled="!formData.proposedInspectionDate"
            @input="handleTimeInput"
            @validation-change="handleTimeValidation"
          />
        </div>
      </div>
      <!-- Date Error Message -->
      <div v-if="formErrors.proposedInspectionDate" class="error-message">
        {{ formErrors.proposedInspectionDate }}
      </div>
    </div>

    <!-- CC Recipients Section - Only show when NOT replying to selected email -->
    <AdditionalRecipientsStandalone 
      v-if="!formData.replyToSelectedEmail"
      ref="recipientsComponent"
      :showDescription="false"
      @selection-changed="handleRecipientSelectionChanged"
    />

    <!-- Signature Field -->
    <div class="form-field">
      <label class="field-label">Signature*</label>
      <div class="signature-container">
        <!-- Vue3 Rich Text Editor -->
        <VueEditor
          ref="signatureEditor"
          key="signatureEditor"
          class="signature-vue-editor"
          v-model="formData.signature"
          :editorToolbar="customToolbar"
          :editorOptions="editorOptions"
          placeholder="Enter your signature here..."
          @ready="onEditorReady"
          @input="onSignatureInput"
          @text-change="onSignatureTextChange"
        />
        
        <!-- Character Count -->
        <div class="signature-char-count" :class="{ error: signatureLength > 500 }">
          {{ signatureLength }}/500 characters
        </div>
        
        <!-- Error Message -->
        <div v-if="formErrors.signature" class="error-message">
          {{ formErrors.signature }}
        </div>
      </div>
    </div>

    </div> <!-- End form-disabled wrapper -->

  </div> <!-- End client-communication-wrapper -->
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DateComponent from '../commons/DateComponent.vue';
import TimePickerComponent from '../commons/TimePickerComponent.vue';
import SelectedEmailAndAssignment from '../commons/SelectedEmailAndAssignment.vue';
import IsLoadingComponent from '../IsLoadingComponent.vue';
import IntroParagraphSelector from '../common/IntroParagraphSelector.vue';
import DataRequestSelector from '../common/DataRequestSelector.vue';
import AdditionalRecipientsStandalone from './AdditionalRecipientsStandalone.vue';
import { VueEditor } from 'vue3-editor';
import { generateEmailTemplate, EMAIL_TEMPLATES } from '../../service/emailTemplates.js';
import { getGraphToken } from '@/service/TokenService.js';
import { saveAssignmentOverviewData, clearAssignmentCache } from '../../service/engagement.js';
import { ACTION_TYPES } from '../../constants.js';
import { handleRecentActions } from "@/service/engagement.js";
import { markEmailAsCompleteNew } from '../../service/Actions.js';
import { addProcessingTime } from '../../utils/metricsHelper.js';
import { getPropertyAddressOrName } from '@/utils/globalUtil.js';
import moment from 'moment';
export default {
  name: 'ClientCommunication',
  components: {
    DateComponent,
    TimePickerComponent,
    SelectedEmailAndAssignment,
    IsLoadingComponent,
    IntroParagraphSelector,
    DataRequestSelector,
    AdditionalRecipientsStandalone,
    VueEditor
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    actionData: {
      type: Object,
      default: () => ({})
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      searchableTemplate: 'Property Contact Request',
      inputDate: '', // For DateComponent
      isSearching: false,
      searchQuery: '',
      emailTemplateOptions: Object.keys(EMAIL_TEMPLATES),
      formData: {
        replyToSelectedEmail: false,
        dataRequest: null,
        selectedDataRequestObject: null,
        selectedIntroParagraph: 'default',
        selectedIntroParagraphObject: null,
        propertyContactFirstName: '',
        propertyContactLastName: '',
        propertyContactEmail: '',
        proposedInspectionDate: '',
        proposedInspectionTime: '',
        signature: ''
      },
      selectedCCEmails: [],
      formErrors: {
        proposedInspectionDate: '',
        propertyContactEmail: '',
        signature: '',
        timeValidation: false // Track time validation state
      },
      showLoading: false, // Loading state for save operation
      isLoadingPropertyContacts: false, // Local loading state for assignment changes
      isLoadingSignature: false, // Loading state for signature operations
      isEditorReady: false, // Track if Vue editor is ready
      
      // Signature field state
      isSignatureFocused: false,
      
      // Vue3 Editor Configuration
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['clean']
      ],
      
      // Editor options to enable rich text editing
      editorOptions: {
        debug: false,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean']
          ],
          history: {
            delay: 1000,
            maxStack: 50,
            userOnly: false
          },
          clipboard: {
            matchVisual: false
          }
        },
        theme: 'snow',  // Use snow theme to show toolbar
        strict: false,
        scrollingContainer: null,
        bounds: null,
        readOnly: false,
        placeholder: 'Enter your signature here...',
        formats: ['bold', 'italic', 'underline', 'link', 'list']
      },
      
      isLoadingDataRequest: false, // Local loading state for data request options
      propertyTypeOptions: [
        'Select a Data Request List',
        'Auto Dealership Existing',
        'Auto Dealership Proposed',
        'Golf - Private',
        'Golf - Semi-Private',
        'Healthcare Existing',
        'Healthcare Proposed',
        'Hotel/Motel Existing',
        'Hotel/Motel Proposed',
        'Indust./ST Fee Simple Existing',
        'Indust./ST Fee Simple Proposed',
        'Indust./ST Leased Fee Existing',
        'Indust./ST Leased Fee Proposed',
        'Indust./MT Existing',
        'Industrial/MT Proposed',
        'Land Vacant',
        'Office / MT Existing',
        'Office / MT Proposed',
        'Office/ST Fee Simple Existing',
        'Office/ST Fee Simple Proposed',
        'Office/ST Leased Fee Existing',
        'Office/ST Leased Fee Proposed',
        'Reside. Mobile Home Park Existing',
        'Reside. Mobile Home Park Proposed',
        'Residential Multifamily Existing',
        'Residential Multifamily Proposed',
        'Residential Subdivision Existing',
        'Residential Subdivision Proposed',
        'Retail/MT Existing',
        'Retail/MT Proposed',
        'Retail/ST Fee Simple Existing',
        'Retail/ST Fee Simple Proposed',
        'Retail/ST Leased Fee Existing',
        'Retail/ST Leased Fee Proposed',
        'Self-Storage Existing',
        'Self-Storage Proposed'
      ],
      processingStartTime: null,
      emailContentSubject: null,
      finalDataRequestOptions: [],
      isTimeValid: true, // Track time validation state
      userEnteredDate: false, // Track if user manually entered date
      userEnteredTime: false, // Track if user manually entered time
    };
  },
  created() {
    // Fetch all lookups when component is created
    this.$store.dispatch('lookup/fetchAllLookups');
  },
  computed: {
    ...mapGetters({
      selectedAssignment: 'assignments/selectedAssignment',
      emailData: 'email/currentEmail',
      assignmentOverviewData: 'assignments/assignmentOverviewData',
      getUserDetails: "email/getUserDetails",
      isLoadingAssignmentData: 'assignments/isLoading',
      getAllLookups: "lookup/getAllLookups",
    }),
    propertyTypeOptionsFiltered() {
      return this.finalDataRequestOptions.filter(option => option !== 'Select a Data Request List');
    },

    isFormValid() {
      // Check mandatory fields
      const { dataRequest, propertyContactFirstName, propertyContactLastName, propertyContactEmail, proposedInspectionDate, signature } = this.formData;
      
      // Data request validation
      const dataRequestValid = dataRequest && dataRequest.trim();
      
      // Contact name validation - only first name is required now
      const nameValid = propertyContactFirstName?.trim();
      
      // Email validation
      const emailValid = propertyContactEmail?.trim() && this.isValidEmail(propertyContactEmail);
      
      // Date validation (optional field)
      const dateValid = !proposedInspectionDate || this.isValidDate(proposedInspectionDate);
      
      // Enhanced signature validation (required) - check for meaningful content
      let signatureValid = false;
      if (signature && signature.trim()) {
        // Remove HTML tags and check if there's actual text content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = signature;
        const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
        signatureValid = textContent.length > 0;
      }
      
      // No validation errors
      const noErrors = !this.formErrors.proposedInspectionDate && 
                      !this.formErrors.propertyContactEmail && 
                      !this.formErrors.signature;
      
      const isValid = !!(dataRequestValid && nameValid && emailValid && dateValid && signatureValid && noErrors && this.isTimeValid);
      return isValid;
    },

    hasEmailForReply() {
      return !!(this.emailData && this.emailData.subject && this.emailData.subject.trim());
    },

    formattedSignature() {
      if (!this.formData.signature) return '';
      
      // Convert line breaks to HTML and apply basic formatting
      return this.formData.signature
        .replace(/\n/g, '<br>')
        .replace(/\|/g, ' | ') // Add spacing around pipes
        .replace(/^(.+)$/gm, '<div>$1</div>'); // Wrap each line in div
    },

    signatureLength() {
      if (!this.formData.signature) return 0;
      // Get plain text length (strip HTML tags)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.formData.signature || '';
      const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
      return textContent.length;
    }
  },
  watch: {
    isFormValid(newValue) {
      this.$emit('isValidForSubmit', newValue);
    },
    assignmentOverviewData: {
      handler(newData, oldData) {
        
        if (newData) {
          this.populateFormFields(newData);
        } else {
          this.clearFormFields();
        }
      },
      immediate: true,
      deep: true
    },
    finalDataRequestOptions: {
      handler(newOptions) {
        // Don't auto-select anything - let user choose or let assignment data populate it
        // Only clear selection if options list becomes empty
        if (!newOptions || newOptions.length === 0) {
          this.formData.dataRequest = null;
        }
      },
      immediate: true
    },
    formData: {
      handler() {
        this.$nextTick(() => {
          this.$emit('isValidForSubmit', this.isFormValid);
        });
      },
      deep: true
    },
    visible: {
      async handler(newValue, oldValue) {
        // When dialog becomes visible, always fetch latest assignment information
        if (newValue && !oldValue) {
          // Reset flags when dialog opens to allow loading fresh data from server
          this.userEnteredDate = false;
          this.userEnteredTime = false;
          
          this.isLoadingPropertyContacts = true;
          try {
            // Always fetch lookups when popup opens
            await this.$store.dispatch('lookup/fetchAllLookups');
            // Always fetch latest assignment data regardless of what's in store
            if (this.selectedAssignment && this.selectedAssignment.viewEventId) {
              // Clear cache to force fresh fetch from server
              clearAssignmentCache(this.selectedAssignment.viewEventId);
              await this.$store.dispatch('assignments/fetchAssignmentOverviewData', this.selectedAssignment.viewEventId);
              // Rebuild data request options with fresh assignment data
              await this.buildFinalDataRequestOptions();
              // Populate form with the latest data
              await this.$nextTick();
              const freshData = this.$store.getters['assignments/assignmentOverviewData'];
              if (freshData) {
                this.populateFormFields(freshData);
              } 
            }
          } catch (error) {
            console.error('❌ Failed to fetch latest assignment data on dialog open:', error);
            this.$emit('showNotification', {
              type: 'warning',
              message: 'Failed to load latest assignment data. Using cached information.'
            });
          } finally {
            this.isLoadingPropertyContacts = false;
            console.log('🔄 ClientCommunication dialog data refresh completed');
          }
        }
      }
    },
    selectedAssignment: {
      immediate: true,
      handler(newAssignment) {
        if (newAssignment && newAssignment.userFriendlyId) {
          this.formData.dataRequest = null;
          this.buildFinalDataRequestOptions();
        }
      }
    },

    emailData: {
      immediate: true,
      handler(newEmail, oldEmail) {
        // Reset reply checkbox when email selection changes
        if (newEmail !== oldEmail) {
          this.formData.replyToSelectedEmail = false;
          // Emit close dialog when selected email changes (but not on initial load)
          if (oldEmail !== undefined) {
            this.$emit('close');
          }
        }
      }
    }
  },
  async mounted() {
    this.processingStartTime = performance.now();
    
    // Always fetch fresh assignment data when component is mounted
    if (this.selectedAssignment && this.selectedAssignment.viewEventId) {
      this.isLoadingPropertyContacts = true;
      try {
        // Clear cache to ensure fresh fetch from server
        clearAssignmentCache(this.selectedAssignment.viewEventId);
        // Fetch latest assignment overview data
        await this.$store.dispatch('assignments/fetchAssignmentOverviewData', this.selectedAssignment.viewEventId);
        // Rebuild data request options with fresh assignment data
        await this.buildFinalDataRequestOptions();
        // Populate form with the latest data
        await this.$nextTick();
        const freshData = this.$store.getters['assignments/assignmentOverviewData'];
        if (freshData) {
          this.populateFormFields(freshData);
        }
      } catch (error) {
        console.error('❌ Failed to fetch latest assignment data on mount:', error);
        this.$emit('showNotification', {
          type: 'warning',
          message: 'Failed to load latest assignment data. Using cached information.'
        });
      } finally {
        this.isLoadingPropertyContacts = false;
      }
    }
    
    this.$nextTick(() => {
    // Add comprehensive error handler for Quill.js errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Suppress various Quill.js and MutationObserver errors
      const errorString = args.join(' ').toLowerCase();
      if (
        errorString.includes('cannot read properties of undefined') ||
        errorString.includes('mutationobserver') ||
        errorString.includes('scroll.update') ||
        errorString.includes('reading \'emit\'') ||
        errorString.includes('quill') ||
        errorString.includes('taskpane.js:167586') ||
        errorString.includes('taskpane.js:170376') ||
        errorString.includes('leaf.position is not a function') ||
        errorString.includes('failed to execute \'setstart\' on \'range\'') ||
        errorString.includes('indexsizeerror')
      ) {
        return; // Ignore these specific errors
      }
      originalConsoleError.apply(console, args);
    };
  });
  
    // Override window.onerror to catch uncaught errors
    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      const errorString = (message || '').toLowerCase();
      if (
        errorString.includes('cannot read properties of undefined') ||
        errorString.includes('reading \'emit\'') ||
        errorString.includes('mutationobserver') ||
        (source && source.includes('taskpane.js'))
      ) {
        return true; // Prevent default error handling
      }
      if (originalOnError) {
        return originalOnError.call(window, message, source, lineno, colno, error);
      }
      return false;
    };

    // Override unhandledrejection to catch promise errors
    window.addEventListener('unhandledrejection', (event) => {
      const errorString = (event.reason?.message || event.reason || '').toLowerCase();
      if (
        errorString.includes('cannot read properties of undefined') ||
        errorString.includes('reading \'emit\'') ||
        errorString.includes('mutationobserver') ||
        errorString.includes('failed to execute \'setstart\' on \'range\'') ||
        errorString.includes('indexsizeerror') ||
        errorString.includes('leaf.position is not a function')
      ) {
        event.preventDefault(); // Prevent unhandled rejection error
      }
    });
    
    // Monkey patch MutationObserver to prevent Quill.js scroll errors
    const originalMutationObserver = window.MutationObserver;
    window.MutationObserver = function(callback) {
      const wrappedCallback = function(mutations, observer) {
        try {
          return callback.call(this, mutations, observer);
        } catch (error) {
          // Silently ignore MutationObserver callback errors
          return;
        }
      };
      return new originalMutationObserver(wrappedCallback);
    };
    
    // Copy static properties
    Object.setPrototypeOf(window.MutationObserver, originalMutationObserver);
    Object.getOwnPropertyNames(originalMutationObserver).forEach(name => {
      if (typeof originalMutationObserver[name] !== 'function') {
        Object.defineProperty(window.MutationObserver, name, 
          Object.getOwnPropertyDescriptor(originalMutationObserver, name));
      }
    });
    
    // Wait for DOM to be fully ready before initializing editor
    await this.$nextTick();
    
    // Load signature after editor is ready - simplified approach
    setTimeout(async () => {
      try {
        // Wait for editor to be fully ready
        let retries = 0;
        while (!this.isEditorReady && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 200));
          retries++;
        }
        await this.loadSignatureFromUserDetails();
      } catch (error) {
        console.error('❌ Error in signature loading process:', error);
        await this.createBasicSignature();
        this.$emit('showNotification', {
          type: 'warning',
          message: 'Could not load signature automatically. Using basic signature.'
        });
      }
    }, 2500); // Longer delay for safety
    
    // Load property data if assignment is already selected
    this.$nextTick(async () => {
      if (this.selectedAssignment && this.selectedAssignment.viewEventId) {
        try {
          this.isLoadingPropertyContacts = true;
          
          // Force fetch assignment overview data
          await this.$store.dispatch('assignments/fetchAssignmentOverviewData', this.selectedAssignment.viewEventId);
          
          // Wait for data to be available
          await this.$nextTick();
          const freshData = this.$store.getters['assignments/assignmentOverviewData'];
          
          if (freshData) {
            console.log('✅ Assignment data loaded, populating form fields');
            this.populateFormFields(freshData);
          } else {
            console.warn('⚠️ No assignment data received after fetch');
          }
        } catch (error) {
          this.$emit('showNotification', {
            type: 'warning',
            message: 'Could not load property data automatically.'
          });
        } finally {
          this.isLoadingPropertyContacts = false;
        }
      } else {
        console.log('📋 No assignment selected on mount');
      }
      
      // Always reset intro paragraph selection to default when popup opens
      this.formData.selectedIntroParagraph = 'default';
      this.formData.selectedIntroParagraphObject = null;
      
      this.$emit('isValidForSubmit', this.isFormValid);
    });
  },
  methods: {
    handleRecipientSelectionChanged({ selectedEmails }) {
      this.selectedCCEmails = selectedEmails;
    },

    handleSearch(searchValue) {
      this.searchQuery = searchValue || '';
      this.isSearching = !!searchValue;
    },

    // Intro paragraph event handlers
    onIntroParagraphChanged(paragraph) {
      console.log('📝 Intro paragraph changed:', paragraph.name);
      this.formData.selectedIntroParagraphObject = paragraph;
    },

    handleIntroParagraphError(message) {
      console.error('❌ Intro paragraph error:', message);
      this.$emit('showNotification', {
        type: 'error',
        message: message || 'Error with intro paragraph'
      });
    },

    handleIntroParagraphSuccess(message) {
      console.log('✅ Intro paragraph success:', message);
      if (message) {
        this.$emit('showNotification', {
          type: 'success',
          message: message
        });
      }
    },

    // Data request event handlers
    onDataRequestChanged(dataRequestName, customRequestObject) {
      console.log('📋 Data request changed:', dataRequestName, customRequestObject);
      
      // If a custom request object is provided (second parameter), use it
      if (customRequestObject && customRequestObject.content) {
        this.formData.selectedDataRequestObject = customRequestObject;
      } else {
        // It's a system request, clear the custom object
        this.formData.selectedDataRequestObject = null;
      }
    },

    handleDataRequestError(message) {
      console.error('❌ Data request error:', message);
      if (message) {
        this.$emit('showNotification', {
          type: 'error',
          message: message || 'Error with data request'
        });
      }
    },

    handleDataRequestSuccess(message) {
      console.log('✅ Data request success:', message);
      // Only emit notification for actual success messages (not empty strings)
      if (message && message.trim()) {
        this.$emit('showNotification', {
          type: 'success',
          message: message
        });
      }
    },

    // Handle editor ready event - simplified approach
    onEditorReady(quill) {
      try {
        this.isEditorReady = true;
        
        if (quill) {
          // Basic safety overrides without disabling toolbar functionality
          const originalGetBounds = quill.selection?.getBounds;
          if (originalGetBounds) {
            quill.selection.getBounds = function(index, length) {
              try {
                return originalGetBounds.call(this, index, length);
              } catch (error) {
                console.warn('GetBounds error prevented:', error.message);
                return { bottom: 0, height: 20, left: 0, right: 0, top: 0, width: 0 };
              }
            };
          }
          
        }
        
      } catch (error) {
        console.warn('Error in onEditorReady:', error.message);
        this.isEditorReady = true; // Still mark as ready
      }
    },

    // Handle input changes
    onSignatureInput(content) {
      try {
        this.formData.signature = content || '';
        
        // Auto-save signature when changed
        if (content && content.trim()) {
          this.saveSignatureForFutureUse(content);
        }
        
        // Enhanced validation for signature
        if (content && content.trim()) {
          // Check for meaningful text content (not just HTML tags)
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = content;
          const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
          
          if (textContent.length === 0) {
            this.formErrors.signature = 'Signature cannot be empty';
          } else if (this.signatureLength > 500) {
            this.formErrors.signature = 'Signature is too long. Please keep it under 500 characters.';
          } else {
            this.formErrors.signature = '';
          }
        } else {
          this.formErrors.signature = 'Signature is required';
        }
        
      } catch (error) {
        console.warn('Error in signature input handler:', error.message);
        // Ensure signature is not cleared on error
        if (content) {
          this.formData.signature = content;
        }
      }
    },

    // Vue3 Editor signature methods - simplified
    onSignatureTextChange() {
      try {
        // This method is called when the editor content changes
        // The content is automatically bound to formData.signature via v-model
        // Auto-save signature when changed
        if (this.formData.signature && this.formData.signature.trim()) {
          this.saveSignatureForFutureUse(this.formData.signature);
        }
        
        // Enhanced validation for signature
        if (this.formData.signature && this.formData.signature.trim()) {
          // Check for meaningful text content (not just HTML tags)
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.formData.signature;
          const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
          
          if (textContent.length === 0) {
            this.formErrors.signature = 'Signature cannot be empty';
          } else if (this.signatureLength > 500) {
            this.formErrors.signature = 'Signature is too long. Please keep it under 500 characters.';
          } else {
            this.formErrors.signature = '';
          }
        } else {
          this.formErrors.signature = 'Signature is required';
        }
      } catch (error) {
        console.warn('Error in signature text change handler:', error.message);
        // Clear error state to prevent UI issues
        this.formErrors.signature = '';
      }
    },

    // Simplified safe signature update method
    safeUpdateSignature(content) {
      try {
        if (!content || content.trim() === '') {
          // Don't clear the signature if empty content is provided, unless it's intentional
          if (content === '') {
            this.formData.signature = '';
          }
          return;
        }
        
        // Simple direct assignment - let Vue handle reactivity
        this.formData.signature = content;
        
        // Ensure the editor is properly updated
        this.ensureSignatureDisplayed();
      } catch (error) {
        console.error('Error in safeUpdateSignature:', error.message);
        // Fallback to direct assignment only if we have content
        if (content) {
          this.formData.signature = content;
          this.ensureSignatureDisplayed();
        }
      }
    },

    // Master signature loading method - prioritize roaming settings first
    async loadSignatureFromUserDetails() {
      this.isLoadingSignature = true;
      try {
        // Priority 1: Check roaming settings first (user's saved preference)
        const roamingSuccess = await this.loadSignatureFromRoamingSettings();
        if (roamingSuccess && this.formData.signature) {
          this.ensureSignatureDisplayed();
          this.isLoadingSignature = false;
          return;
        }
        
        // Priority 2: Try to create from user details API (for fresh data)
        const userEmail = Office.context.mailbox?.userProfile?.emailAddress;
        
        if (userEmail) {
          try {
            const { fetchUserDetails } = await import('../../service/engagement.js');
            const userDetailsFromAPI = await fetchUserDetails(userEmail);
            
            if (userDetailsFromAPI) {
              const signatureHtml = await this.createSignatureFromAPIData(userDetailsFromAPI);
              this.safeUpdateSignature(signatureHtml);
              
              // Save this newly created signature for future use
              await this.saveSignatureForFutureUse(signatureHtml);
              this.ensureSignatureDisplayed();
              this.isLoadingSignature = false;
              return;
            }
          } catch (apiError) {
            console.warn('⚠️ Could not fetch user details from API:', apiError.message);
            // Continue to fallback options
          }
        }
        
        // Priority 3: Try to load from localStorage
        const localSuccess = await this.loadSignatureFromLocalStorage();
        if (localSuccess && this.formData.signature) {
          // Save to roaming settings for future use
          await this.saveSignatureForFutureUse(this.formData.signature);
          this.ensureSignatureDisplayed();
          this.isLoadingSignature = false;
          return;
        }
        
        // Priority 4: Create basic signature as final fallback
        await this.createBasicSignature();
        this.ensureSignatureDisplayed();
        
      } catch (error) {
        console.warn('Error loading signature:', error.message);
        // Show user-friendly notification instead of throwing
        this.$emit('showNotification', {
          type: 'warning',
          message: 'Could not load signature. Using basic signature.'
        });
        await this.createBasicSignature();
        this.ensureSignatureDisplayed();
      } finally {
        this.isLoadingSignature = false;
      }
    },

    // Ensure signature is properly displayed in the editor
    ensureSignatureDisplayed() {
      this.$nextTick(() => {
        try {
          const editor = this.$refs.signatureEditor;
          if (editor && editor.quill && this.formData.signature) {
            // Get current editor content
            const currentContent = editor.quill.root.innerHTML;
            const expectedContent = this.formData.signature;
            
            // Only update if content is different
            if (currentContent !== expectedContent) {
              // Use Quill's API to set content properly
              editor.quill.clipboard.dangerouslyPasteHTML(expectedContent);
              
              // Alternative method if the above doesn't work
              if (editor.quill.root.innerHTML !== expectedContent) {
                editor.quill.root.innerHTML = expectedContent;
              }
            }
            
          } 
        } catch (error) {
          console.error('Error ensuring signature display:', error.message);
          
          // Fallback: try direct DOM manipulation
          try {
            const editor = this.$refs.signatureEditor;
            if (editor && editor.$el) {
              const editorDiv = editor.$el.querySelector('.ql-editor');
              if (editorDiv && this.formData.signature) {
                editorDiv.innerHTML = this.formData.signature;
              }
            }
          } catch (fallbackError) {
            console.error('Fallback signature display also failed:', fallbackError.message);
          }
        }
      });
    },

    // Create signature from API data
    async createSignatureFromAPIData(userDetails) {
      const name = userDetails.fullName || userDetails.displayName || '[Name]';
      const title = userDetails.jobTitle || userDetails.title || '[Your Title]';
      let role = userDetails.role || '[Role]';
      const division = userDetails.divisionName || '[Division]';
      const phone = userDetails.phoneNumber  || '[Phone]';
      const email = userDetails.emailAddress || '[Email]';
      
      // Fetch detailed role information if viewUserRoleId is available
      if (userDetails.viewUserRoleId) {
        try {
          const { fetchUserRoleDetails } = await import('../../service/engagement.js');
          const roleDetails = await fetchUserRoleDetails(userDetails.viewUserRoleId);
          if (roleDetails && roleDetails.viewUserRole) {
            role = roleDetails.viewUserRole;
          }
        } catch (error) {
          console.warn('Could not fetch role details:', error.message);
          // Keep the original role or placeholder
        }
      }
      
      // Format office address
      const officeAddress = userDetails?.officeAddress || userDetails?.address || '[Office Address, City, State, Zipcode]';
      
      // Format phone - only add T prefix if it's actual phone data, not placeholder
      const formattedPhone = (phone !== '[Phone]' && phone && !phone.startsWith('T ')) ? `T ${phone}` : phone;
      
      // Return signature with placeholder format
      return `${name}
${title}
${role} | ${division}
${officeAddress}
${formattedPhone}
${email}`;
    },

    // Create basic signature fallback
    async createBasicSignature() {
      try {
        // Get user details from API only
        const userEmail = Office.context.mailbox?.userProfile?.emailAddress;
        
        if (userEmail) {
          const { fetchUserDetails } = await import('../../service/engagement.js');
          const userDetailsFromAPI = await fetchUserDetails(userEmail);
          
          if (userDetailsFromAPI) {
            // Use API data to create signature (now async)
            const signatureHtml = await this.createSignatureFromAPIData(userDetailsFromAPI);
            this.safeUpdateSignature(signatureHtml);
            return;
          }
        }
        
        // If API fails or no email, create empty signature
        console.warn('Could not fetch user details from API for signature creation');
        this.safeUpdateSignature('');
        
      } catch (error) {
        console.warn('Error creating signature from API:', error.message);
        // Create empty signature instead of using Office profile
        this.safeUpdateSignature('');
      }
    },

    // Manual method to force signature reload (for testing)
    async forceReloadSignature() {
      this.formData.signature = '';
      await this.loadSignatureFromUserDetails();
    },

    clearSignature() {
      try {
        this.formData.signature = '';
        const editor = this.$refs.signatureEditor;
        if (editor && editor.quill) {
          const quill = editor.quill;
          quill.setText(''); // Clear the editor content
          quill.focus(); // Focus the editor
        }
      } catch (error) {
        console.warn('Error clearing signature editor:', error);
        // Even if editor fails, ensure form data is cleared
        this.formData.signature = '';
      }
    },

    // Alternative signature loading methods (no MailboxSettings.Read permission required)
    
    // Method 1: Try to extract signature from Office.js compose mode
    async loadSignatureFromOfficeCompose() {
      try {
        if (Office.context.mailbox && Office.context.mailbox.item) {
          // Check if we're in compose mode
          if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Message) {
            // Try to get the body to see if there's an existing signature
            Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const bodyContent = result.value;
                // Look for signature patterns in the body
                const signatureMatch = this.extractSignatureFromHtml(bodyContent);
                if (signatureMatch) {
                  this.formData.signature = signatureMatch;
                  console.log('✅ Extracted signature from compose body');
                  return true;
                }
              }
            });
          }
        }
      } catch (error) {
        console.warn('Could not load signature from Office compose mode:', error);
      }
      return false;
    },

    // Method 2: Use Office.js roaming settings to store/retrieve user's preferred signature
    async loadSignatureFromRoamingSettings() {
      try {
        if (Office.context.roamingSettings) {
          const savedSignature = Office.context.roamingSettings.get('userSignature');
          if (savedSignature && savedSignature.trim()) {
            this.safeUpdateSignature(savedSignature);
            // Verify it was actually set
            await this.$nextTick();
            if (this.formData.signature && this.formData.signature.trim()) {
              return true;
            } else {
              console.warn('⚠️ Signature not properly set from roaming settings');
              return false;
            }
          }
        }
      } catch (error) {
        console.warn('Could not load signature from roaming settings:', error.message);
      }
      return false;
    },

    // Method 3: Use localStorage as fallback for signature persistence
    async loadSignatureFromLocalStorage() {
      try {
        const userEmail = this.getUserDetails?.emailAddress || Office.context.mailbox?.userProfile?.emailAddress;
        if (userEmail) {
          const storageKey = `outlookSignature_${userEmail}`;
          const savedSignature = localStorage.getItem(storageKey);
          if (savedSignature) {
            this.safeUpdateSignature(savedSignature);
            // Verify it was actually set
            await this.$nextTick();
            if (this.formData.signature && this.formData.signature.trim()) {
              return true;
            } 
          }
        }
      } catch (error) {
        console.warn('Could not load signature from localStorage:', error);
      }
      return false;
    },

    // Method 4: Try to get signature from current email being replied to/forwarded
    async loadSignatureFromCurrentEmail() {
      try {
        if (this.selectedEmail && this.selectedEmail.body) {
          // Look for signature patterns in the selected email body
          const signatureMatch = this.extractSignatureFromHtml(this.selectedEmail.body);
          if (signatureMatch) {
            this.formData.signature = signatureMatch;
            return true;
          }
        }
      } catch (error) {
        console.warn('Could not extract signature from current email:', error);
      }
      return false;
    },

    // Helper method to extract signature from HTML content
    extractSignatureFromHtml(htmlContent) {
      if (!htmlContent) return null;

      // Common signature patterns to look for
      const signaturePatterns = [
        // Look for content after "-- " (standard email signature delimiter)
        /--\s*<br[^>]*>(.*)$/i,
        // Look for content in signature divs
        /<div[^>]*signature[^>]*>(.*?)<\/div>/i,
        // Look for content after multiple line breaks (usually signature)
        /<br[^>]*>\s*<br[^>]*>\s*<br[^>]*>(.*?)$/i,
        // Look for content with typical signature structure (name, title, company)
        /<div[^>]*>([^<]*(?:Manager|Director|Associate|Senior|VP|President|CEO|CTO|CFO)[^<]*)<\/div>/i
      ];

      for (const pattern of signaturePatterns) {
        const match = htmlContent.match(pattern);
        if (match && match[1]) {
          const signature = match[1].trim();
          // Clean up the signature (remove excessive whitespace, etc.)
          if (signature.length > 10 && signature.length < 1000) {
            return this.cleanSignatureHtml(signature);
          }
        }
      }
      return null;
    },

    // Clean and format signature HTML
    cleanSignatureHtml(signatureHtml) {
      // Remove script tags and other potentially harmful content
      let cleaned = signatureHtml
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        .replace(/<style[^>]*>.*?<\/style>/gi, '')
        .replace(/on\w+="[^"]*"/gi, ''); // Remove event handlers

      // Remove ALL br tags completely
      cleaned = cleaned
        .replace(/<br[^>]*>/gi, '') // Remove all br tags
        .replace(/\s+/g, ' ') // Normalize spacing
        .trim();

      return cleaned;
    },

    // Preserve rich text formatting specifically for email
    preserveRichTextFormatting(signatureHtml) {
      // If it's plain text, just add br tags
      if (!signatureHtml.includes('<') && !signatureHtml.includes('>')) {
        return signatureHtml.replace(/\n/g, '<br>');
      }

      // Clean up the HTML while preserving all formatting tags
      let cleaned = signatureHtml
        // First, handle div to div transitions with br tags
        .replace(/<\/div>\s*<div[^>]*>/gi, '</div><br><div>')
        // Then remove all div tags but keep the br tags we just added
        .replace(/<div[^>]*>/gi, '')
        .replace(/<\/div>/gi, '')
        // Handle p tags the same way
        .replace(/<\/p>\s*<p[^>]*>/gi, '</p><br><p>')
        .replace(/<p[^>]*>/gi, '')
        .replace(/<\/p>/gi, '')
        // Convert any remaining newlines to br tags
        .replace(/\n/g, '<br>')
        // Clean up multiple consecutive br tags
        .replace(/(<br>\s*){2,}/gi, '<br>')
        // Remove trailing br
        .replace(/<br>\s*$/, '')
        // Clean up whitespace
        .replace(/\s+/g, ' ')
        .trim();

      return cleaned;
    },

    // Save signature for future use
    async saveSignatureForFutureUse(signature) {
      try {
        const userEmail = this.getUserDetails?.emailAddress || Office.context.mailbox?.userProfile?.emailAddress;
        
        // Save to roaming settings (syncs across devices)
        if (Office.context.roamingSettings) {
          Office.context.roamingSettings.set('userSignature', signature);
          Office.context.roamingSettings.saveAsync();
        }

        // Save to localStorage as backup
        if (userEmail) {
          const storageKey = `outlookSignature_${userEmail}`;
          localStorage.setItem(storageKey, signature);
        }
      } catch (error) {
        console.warn('Could not save signature:', error);
      }
    },

    // Master method to try all signature loading alternatives
    async loadAlternativeSignature() {
      this.isLoadingSignature = true;
      try {
        // Try methods in order of preference
        const methods = [
          this.loadSignatureFromRoamingSettings,
          this.loadSignatureFromOfficeCompose, 
          this.loadSignatureFromCurrentEmail,
          this.loadSignatureFromLocalStorage
        ];

        for (const method of methods) {
          const success = await method.call(this);
          if (success && this.formData.signature) {
            // Auto-save the signature for future use
            await this.saveSignatureForFutureUse(this.formData.signature);
            return true;
          }
        }

        return true;
        
      } catch (error) {
        console.error('Failed to load alternative signature:', error);
      } finally {
        this.isLoadingSignature = false;
      }
    },

    htmlToPlainText(html) {
      // Create a temporary element to parse HTML
      const temp = document.createElement('div');
      temp.innerHTML = html;
      
      // Get text content and clean up
      let text = temp.textContent || temp.innerText || '';
      
      // Clean up extra whitespace and normalize line breaks
      text = text
        .replace(/\s+/g, ' ')
        .replace(/\n\s*\n/g, '\n')
        .trim();
      
      return text;
    },

    ...mapActions({ 
      addMetricsData: "assignments/addMetricsData",
      fetchDataRequest: "assignments/fetchDataRequest"
    }),
    async handleAssignmentChanged(assignment) {
      this.$emit('assignmentChanged', assignment);
      
      this.clearFormFields();
      
      if (assignment && assignment.viewEventId) {
        this.isLoadingPropertyContacts = true;
        try {
          // Always clear cache and fetch fresh assignment data
          clearAssignmentCache(assignment.viewEventId);
          await this.$store.dispatch('assignments/fetchAssignmentOverviewData', assignment.viewEventId);
          
          // Rebuild data request options with fresh assignment data
          await this.buildFinalDataRequestOptions();
          
          await this.$nextTick();
          const freshData = this.$store.getters['assignments/assignmentOverviewData'];
          
          if (freshData) {
            this.populateFormFields(freshData);
          }
          
        } catch (error) {
          console.error('❌ Failed to fetch assignment overview data:', error);
          this.$emit('showNotification', {
            type: 'error',
            message: 'Failed to load assignment data. Please try again.'
          });
        } finally {
          this.isLoadingPropertyContacts = false;
        }
      }
    },

    clearFormFields() {
      this.formData.replyToSelectedEmail = false;
      this.formData.propertyContactFirstName = '';
      this.formData.propertyContactLastName = '';
      this.formData.propertyContactEmail = '';
      this.formData.proposedInspectionDate = '';
      this.formData.proposedInspectionTime = '';
      this.inputDate = '';
      this.userEnteredDate = false; // Reset flags
      this.userEnteredTime = false;
    },

    async refreshAssignmentDataInBackground(viewEventId) {
      try {
        clearAssignmentCache(viewEventId);
        await this.$store.dispatch('assignments/fetchAssignmentOverviewData', viewEventId);
      } catch (error) {
        console.warn('⚠️ Background refresh failed (non-critical):', error);
      }
    },

    populateFormFields(overviewData) {
      if (!overviewData) {
        return;
      }
      
      // Extract property contact information from valuationAssignmentInformation.propertyContacts
      const valuationInfo = overviewData.valuationAssignmentInformation;
      let propertyContactName = null;
      let propertyContactEmail = null;
      
      if (valuationInfo && valuationInfo.propertyContacts) {
        // Get the first property contact from the propertyContacts array/object
        let propertyContact = null;
        
        if (Array.isArray(valuationInfo.propertyContacts)) {
          // If it's an array, take the first element
          propertyContact = valuationInfo.propertyContacts[0];
        } else if (typeof valuationInfo.propertyContacts === 'object') {
          // If it's an object, use it directly
          propertyContact = valuationInfo.propertyContacts;
        }
        
        if (propertyContact) {
          propertyContactName = propertyContact.propertyContactName || propertyContact.name || propertyContact.fullName;
          propertyContactEmail = propertyContact.propertyContactEmail || propertyContact.email;
        }
      }
      
      // Auto-populate the form fields if data is available, but preserve user input
      if (propertyContactName) {
        // Split the name: if there's a space, split into first and last name
        const nameParts = propertyContactName.trim().split(/\s+/);
        
        if (nameParts.length >= 2) {
          // If there are multiple parts, first part goes to firstName, rest to lastName
          // Only populate if the fields are empty (preserve user input)
          if (!this.formData.propertyContactFirstName.trim()) {
            this.formData.propertyContactFirstName = nameParts[0];
          }
          if (!this.formData.propertyContactLastName.trim()) {
            this.formData.propertyContactLastName = nameParts.slice(1).join(' ');
          }
        } else {
          // If no space, put everything in first name
          if (!this.formData.propertyContactFirstName.trim()) {
            this.formData.propertyContactFirstName = nameParts[0];
          }
          if (!this.formData.propertyContactLastName.trim()) {
            this.formData.propertyContactLastName = '';
          }
        }
      }
      
      // Only populate email if user hasn't entered one yet (preserve user input)
      if (propertyContactEmail && !this.formData.propertyContactEmail.trim()) {
        this.formData.propertyContactEmail = propertyContactEmail;
      }

      // Extract and populate proposed inspection date/time from deadlines.inspectedDate
      // Only populate if user hasn't manually entered values (flags will be false when dialog opens)
      if (overviewData.deadlines && overviewData.deadlines.inspectedDate) {
        const inspectedDateStr = overviewData.deadlines.inspectedDate;
        
        try {
          const dateTimeMatch = inspectedDateStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
          
          if (dateTimeMatch) {
            const [, year, month, day, hours24, minutes] = dateTimeMatch;
            
            const formattedDate = `${month}/${day}/${year}`;
            
            // Populate date if user hasn't manually entered one (flag is false)
            if (!this.userEnteredDate) {
              this.formData.proposedInspectionDate = formattedDate;
              this.inputDate = formattedDate;
              
              // Check if the existing proposed inspection date is in the past
              const proposedDate = new Date(year, month - 1, day);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              
              if (proposedDate < today) {
                // Set error message for past date - use same message as existing validation
                this.formErrors.proposedInspectionDate = 'The date value cannot be a past date for Proposed Inspection Date.';
              } else {
                // Clear any existing error if date is valid
                this.formErrors.proposedInspectionDate = '';
              }
            }
            
            // Populate time if user hasn't manually entered one (flag is false)
            if (!this.userEnteredTime) {
              let hours = parseInt(hours24);
              const mins = parseInt(minutes);
              const period = hours >= 12 ? 'PM' : 'AM';
              
              if (hours === 0) {
                hours = 12;
              } else if (hours > 12) {
                hours -= 12;
              }
              
              const formattedTime = `${hours}:${mins.toString().padStart(2, '0')} ${period}`;
              this.formData.proposedInspectionTime = formattedTime;
            }
          }
        } catch (error) {
          console.warn('Error parsing inspectedDate:', error);
        }
      }
    },

    getClientContactName() {
      return this.assignmentOverviewData?.clientDetails?.clientContact || "";
    },
    
    getClientContactEmail() {
      return this.assignmentOverviewData?.clientDetails?.clientContactEmail || this.selectedAssignment?.clientContactEmail;
    },
    
    onDateInput(val) {
      if (val && val.target && typeof val.target.value === 'string') {
        val = val.target.value;
      }
      if (typeof val !== 'string') return;

      this.inputDate = val;
      this.formData.proposedInspectionDate = val;
      this.userEnteredDate = true; // Mark that user entered date
      
      // Clear time field if date is cleared
      if (!val || val.trim() === '') {
        this.formData.proposedInspectionTime = '';
      }
      
      // Validate date and set error message
      this.validateDate(val);
    },
    async buildFinalDataRequestOptions() {
      if (!this.selectedAssignment || !this.selectedAssignment.userFriendlyId) {
        this.finalDataRequestOptions = [];
        this.formData.dataRequest = null;
        return;
      }
      this.isLoadingDataRequest = true;
      try {
        // Always await lookups to ensure data is loaded before using getAllLookups
        await this.$store.dispatch('lookup/fetchAllLookups');
        await this.$nextTick(); // Wait for computed property to update
        const allLookups = this.getAllLookups;
        if (!allLookups || !Array.isArray(allLookups) || !allLookups.length) {
          this.finalDataRequestOptions = [];
          this.formData.dataRequest = null;
          this.isLoadingDataRequest = false;
          return;
        }
        const dataRequestList = await this.fetchDataRequest(this.selectedAssignment.userFriendlyId);
        const lookupItems = this.getLookupItems('dataRequestList', true); // replace with your lookup type
        const lookupNamesList = lookupItems.map(item => item.name); // <-- This is the list of names
        
        // Check if the assignment has a valid data request
        const assignmentDataRequest = dataRequestList.dataRequestListType.name;
        
        // Always use the full lookup list to maintain the original order
        this.finalDataRequestOptions = [...lookupNamesList];
        
        if (assignmentDataRequest && assignmentDataRequest !== null) {
          // Check if the assignment's data request is in the lookup list
          const isInLookupList = lookupNamesList.includes(assignmentDataRequest);
          
          if (isInLookupList) {
            // Just select it from the existing options without changing order
            this.formData.dataRequest = assignmentDataRequest;
            // Clear any custom data request object since this is a system request
            this.formData.selectedDataRequestObject = null;
          } else {
            // If not in the list, add it to the beginning and select it
            this.finalDataRequestOptions = [assignmentDataRequest, ...lookupNamesList];
            this.formData.dataRequest = assignmentDataRequest;
            // Clear any custom data request object since this is a system request
            this.formData.selectedDataRequestObject = null;
          }
        } else {
          // No valid data request for this assignment - don't pre-select anything
          this.formData.dataRequest = null;
          this.formData.selectedDataRequestObject = null;
        }
      } catch (e) {
        this.finalDataRequestOptions = [];
        this.formData.dataRequest = null;
      } finally {
        this.isLoadingDataRequest = false;
      }
   },

    getLookupItems(lookupType, shouldSort = false, fallbackItem = null) {
      const allLookups = this.getAllLookups;
      if (allLookups && Array.isArray(allLookups)) {
        const lookup = allLookups.find(lookup => lookup.lookupType === lookupType);
        if (lookup && lookup.lookups) {
          const transformedItems = lookup.lookups.map(item => ({
            id: item.keyId,
            name: item.key,
            sortOrder: item.sortOrder
          }));
          
          if (shouldSort) {
            const sortedItems = _.sortBy(transformedItems, 'sortOrder');
            return fallbackItem ? [fallbackItem, ...sortedItems] : [...sortedItems];
          }
          
          return [...transformedItems];
        }
      }
      return fallbackItem ? [fallbackItem] : [];
    },

    validateDate(dateString) {
      if (!dateString) {
        this.formErrors.proposedInspectionDate = '';
        return;
      }

      if (!this.isValidDate(dateString)) {
        // Check if it's a past date specifically
        const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
        if (regex.test(dateString)) {
          const [month, day, year] = dateString.split('/').map(Number);
          const inputDate = new Date(year, month - 1, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (inputDate < today) {
            this.formErrors.proposedInspectionDate = 'The date value cannot be a past date for Proposed Inspection Date.';
          }else{
            this.formErrors.proposedInspectionDate = '';
          }
        }else {
          this.formErrors.proposedInspectionDate = '';
        }
      } else {
        this.formErrors.proposedInspectionDate = '';
      }
    },

    validateEmail(email) {
      if (!email) {
        this.formErrors.propertyContactEmail = '';
        return;
      }

      if (!this.isValidEmail(email)) {
        this.formErrors.propertyContactEmail = 'Please enter a valid email address.';
      } else {
        this.formErrors.propertyContactEmail = '';
      }
    },

    handleTimeInput(timeValue) {
      this.formData.proposedInspectionTime = timeValue?.trim() || '';
      this.userEnteredTime = true; // Mark that user entered time
    },

    handleTimeValidation(validation) {
      this.isTimeValid = validation.isValid;
    },


    
    isValidDate(dateString) {
      if (!dateString) return false;
      
      const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{2}(\d{2})?$/;
      if (!regex.test(dateString)) return false;
      
      const [month, day, year] = dateString.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Allow present and future dates
      return date.getFullYear() === year &&
             date.getMonth() === month - 1 &&
             date.getDate() === day &&
             date >= today;
    },
    
    async startTemplate() {
      // Set loading state
      this.showLoading = true;

      try {
        // DON'T reload signature here - preserve user changes
        // await this.loadSignatureFromUserDetails();
        
        // Validate email and date (if entered) to ensure error messages are set
        if (this.formData.propertyContactEmail) {
          this.validateEmail(this.formData.propertyContactEmail);
        }
        if (this.formData.proposedInspectionDate) {
          this.validateDate(this.formData.proposedInspectionDate);
        }

        // Enhanced signature validation with HTML content check
        if (!this.formData.signature || !this.formData.signature.trim()) {
          this.formErrors.signature = 'Signature is required';
        } else {
          // Check for meaningful text content (not just HTML tags)
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.formData.signature;
          const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
          
          if (textContent.length === 0) {
            this.formErrors.signature = 'Signature cannot be empty';
          } else if (this.signatureLength > 500) {
            this.formErrors.signature = 'Signature is too long. Please keep it under 500 characters.';
          } else {
            this.formErrors.signature = '';
          }
        }

        if (!this.isFormValid) {
          let errorMessage = 'Please fill in all required fields';
          
          // Provide specific error messages for mandatory fields
          if (!this.formData.dataRequest) {
            errorMessage = 'Please select a Data Request';
          } else if (!this.formData.propertyContactFirstName) {
            errorMessage = 'Please enter Property Contact first name';
          } else if (!this.formData.propertyContactEmail) {
            errorMessage = 'Please enter Property Contact email address';
          } else if (this.formErrors.propertyContactEmail) {
            errorMessage = this.formErrors.propertyContactEmail;
          } else if (this.formErrors.proposedInspectionDate) {
            errorMessage = this.formErrors.proposedInspectionDate;
          } else if (!this.formData.signature || !this.formData.signature.trim()) {
            errorMessage = 'Please enter your signature';
          } else if (this.formErrors.signature) {
            errorMessage = this.formErrors.signature;
          }
          
          this.$emit('showNotification', {
            type: 'error',
            message: errorMessage
          });
          return;
        }
        
        // Save data to TOPS2 first, then open email template
        await this.saveAndOpenEmailTemplate();

      } catch (error) {
        
        // Only show error if it's related to email creation, not saving
        const isEmailError = error.message && (
          error.message.includes('email') || 
          error.message.includes('template') ||
          error.message.includes('compose')
        );
        
        if (isEmailError) {
          this.$emit('showNotification', {
            type: 'error',
            message: `Failed to create email template: ${error.message}`
          });
        } else {
          // For other errors, provide a generic message
          this.$emit('showNotification', {
            type: 'error',
            message: 'An unexpected error occurred. Please try again.'
          });
        }
      } finally {
        // Always clear loading state
        this.showLoading = false;
      }
    },
    
    async saveAndOpenEmailTemplate() {
      // Save property contact data first (this is optional - email will still be created if it fails)
      let saveSuccessful = false;
      try {
        await this.savePropertyContactToTOPS2();
        saveSuccessful = true;
      } catch (saveError) {
        console.warn('⚠️ Property contact save failed, but continuing with email creation:', saveError.message);
        
        // Show warning to user but don't block email creation
        this.$emit('showNotification', {
          type: 'warning',
          message: saveError.message || 'Failed to save property contact details to TOPS2. The email will still be created.'
        });
      }
      
      // Open the email template regardless of save status
      await this.openClientCommunicationEmail();
      // Save PCR audit record first so the DB entry exists before re-fetching eligible IDs
      await this.saveRecentAction();
      // Re-fetch PFU eligible IDs AFTER the audit save is committed so the new PCR is included
      this.$store.dispatch('assignments/loadPropertyFollowUpEligibleIds').catch(() => {});
      
      // Show appropriate success message
      if (saveSuccessful) {
        this.$emit('showNotification', {
          type: 'success',
          message: 'Email template created and property details saved successfully!'
        });
      } else {
        this.$emit('showNotification', {
          type: 'info',
          message: 'Email template created successfully. Note: Property contact details were not saved to TOPS2 - please save them manually if needed.'
        });
      }
    },
    
    async savePropertyContactToTOPS2() {
      if (!this.selectedAssignment) {
        throw new Error('Assignment data not available');
      }
      
      // CRITICAL: Always fetch the absolute latest assignment data before saving
      // This prevents overwriting recent changes made by other users or processes
      let latestAssignmentData;
      try {
        // Clear any cached data to force fresh fetch
        clearAssignmentCache(this.selectedAssignment.viewEventId);
        
        // Fetch the latest assignment data directly from server
        const { fetchAssignmentOverviewData } = await import('../../service/engagement.js');
        latestAssignmentData = await fetchAssignmentOverviewData(this.selectedAssignment.viewEventId);
        
        if (!latestAssignmentData) {
          throw new Error('Failed to fetch latest assignment data from server');
        }
        
      } catch (fetchError) {
        console.error('Failed to fetch latest assignment data before save:', fetchError);
        throw new Error('Cannot save: Failed to retrieve latest assignment data. Please try again.');
      }
      
      // Prepare property contact data from the form
      const propertyContactData = {
        propertyContactName: `${this.formData.propertyContactFirstName} ${this.formData.propertyContactLastName}`.trim(),
        propertyContactEmail: this.formData.propertyContactEmail
      };
      
      // Only include proposedInspectionDateTime if date is provided and not empty
      const dateValue = this.formData.proposedInspectionDate;
      const hasValidDate = dateValue && 
                          typeof dateValue === 'string' && 
                          dateValue.trim() !== '' && 
                          dateValue.trim() !== 'null' && 
                          dateValue.trim() !== 'undefined';
      
      if (hasValidDate) {
        propertyContactData.proposedInspectionDateTime = this.combineDateTime(this.formData.proposedInspectionDate, this.formData.proposedInspectionTime);
      } else {
        // Explicitly set to null to clear existing date in backend
        propertyContactData.proposedInspectionDateTime = null;
      }
      // Import the save function
      const { saveAssignmentOverviewData } = await import('../../service/engagement.js');
      
      // Save to TOPS2 using the LATEST assignment overview data with retry logic
      let retryCount = 0;
      const maxRetries = 2;
      let result = null;
      while (retryCount <= maxRetries) {
        try {
          // Use the latest data fetched from server, not stale store data
          result = await saveAssignmentOverviewData(latestAssignmentData, propertyContactData);
          
          // Update the store immediately with the saved changes to keep UI in sync
          await this.$store.dispatch('assignments/fetchAssignmentOverviewData', this.selectedAssignment.viewEventId);
          
          // Success - break out of retry loop
          break;
          
        } catch (saveError) {
          retryCount++;
          
          // Check if this is a retryable error
          const isRetryable = saveError.message.includes('Invalid JSON response') || 
                             saveError.message.includes('SyntaxError') ||
                             saveError.message.includes('HTTP 500');
          
          if (isRetryable && retryCount <= maxRetries) {
            // Wait briefly before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          }
          
          // After all retries failed, provide user-friendly error messages
          if (saveError.message.includes('Invalid JSON response') || saveError.message.includes('SyntaxError')) {
            throw new Error('Server returned invalid response format. The email will still be created, but property contact details were not saved to TOPS2. Please save them manually later.');
          } else if (saveError.message.includes('HTTP 500')) {
            throw new Error('Server error occurred while saving. The email will still be created, but property contact details were not saved to TOPS2. Please try saving them manually later.');
          } else if (saveError.message.includes('HTTP 400')) {
            throw new Error('Invalid data provided. Please check your input and try again.');
          } else if (saveError.message.includes('HTTP 401')) {
            throw new Error('Authentication failed. Please refresh the page and try again.');
          } else if (saveError.message.includes('HTTP 403')) {
            throw new Error('You do not have permission to save this data. Please contact your administrator.');
          } else {
            // For unknown errors, provide a generic message but allow email creation to continue
            console.error('🚨 Unknown save error:', saveError);
            throw new Error(`Save operation failed: ${saveError.message}. The email will still be created, but property contact details were not saved to TOPS2.`);
          }
        }
      }
      
      this.$emit('showNotification', {
        type: 'success',
        message: 'Property contact details saved to TOPS2 successfully!'
      });
      
      // Reset date/time flags after successful save so next time modal opens it loads fresh data
      this.userEnteredDate = false;
      this.userEnteredTime = false;
      
      return result;
    },
    
    combineDateTime(date, time) {
      // This method is only called when date is provided, so no need to check for null date
      
      // If date is provided but no time, set default time to 9:00 AM
      if (!time || time.trim() === '') {
        time = '00:00 AM';
      }
      
      try {
        // Parse the date (MM/DD/YYYY format)
        const [month, day, year] = date.split('/');
        
        // Parse the time (12-hour format with AM/PM like "3:15 PM")
        const timeMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!timeMatch) {
          throw new Error(`Invalid time format: ${time}`);
        }
        
        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const period = timeMatch[3].toUpperCase();
        
        // Convert to 24-hour format
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        // Create ISO datetime string in UTC format
        const dateTimeString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00Z`;
        
        return dateTimeString;
      } catch (error) {
        console.warn('Error combining date and time:', error);
        return null;
      }
    },
    async saveRecentAction() {
      let propertyName =this.assignmentOverviewData?.address?.fullAddress
      if(this.assignmentOverviewData?.address?.propertyName) {
        propertyName = this.assignmentOverviewData?.address?.propertyName
      }
      
      // Use reply email subject if "Reply to Selected Email" is selected, otherwise use new template subject
      let subjectToUse;
      if (this.formData.replyToSelectedEmail && this.emailData && this.emailData.subject) {
        // Create proper reply subject with RE: prefix
        subjectToUse = this.createReplySubject(this.emailData.subject);
      } else {
        const newEmailSubject = `Appraisal of ${propertyName} | ${this.assignmentOverviewData?.address?.fullAddress} } `;
        subjectToUse = this.emailContentSubject || newEmailSubject;
      }
      
      const payload = {
        action: ACTION_TYPES.PROPERTY_CONTACT_REQUEST,
        entityIds: [this.selectedAssignment.viewEventId],
        fileId: this.selectedAssignment.code,
        propertyContact: `${this.formData.propertyContactFirstName} ${this.formData.propertyContactLastName}`.trim(),
        subjectLine: subjectToUse,
        toAddress: this.formData.propertyContactEmail,
      };
      await handleRecentActions(payload, ACTION_TYPES.PROPERTY_CONTACT_REQUEST);
    },

    // Helper method to create proper reply subjects
    createReplySubject(originalSubject) {
      if (!originalSubject || !originalSubject.trim()) {
        return 'RE: ';
      }
      // Remove any existing RE: or FW: prefixes to get the clean subject
      let cleanSubject = originalSubject.trim();
      // Remove RE:, FW:, Fwd:, etc. prefixes (case insensitive)
      cleanSubject = cleanSubject.replace(/^(RE:|FW:|FWD:|Fwd:)\s*/i, '').trim();
      // Also handle multiple nested prefixes like "RE: FW: RE: original subject"
      while (/^(RE:|FW:|FWD:|Fwd:)\s*/i.test(cleanSubject)) {
        cleanSubject = cleanSubject.replace(/^(RE:|FW:|FWD:|Fwd:)\s*/i, '').trim();
      }
      // Return with proper RE: prefix
      return `RE: ${cleanSubject}`;
    },

    async openClientCommunicationEmail() {
      
      let finalStatus = 'Completed';
      let errorMessage = null;
      const metricsPayload = {
        entityId: this.selectedAssignment?.viewEventId,
        fileId: this.selectedAssignment?.code,
        actionTaken: ACTION_TYPES.PROPERTY_CONTACT_REQUEST,
      };
      const userInfo = this.getUserInfo();
      const dynamicData = this.prepareDynamicData();
      try {
        // Try Graph approach with enhanced signatures
        const accessToken = await getGraphToken();
        
        // Prepare dynamic data with selected intro paragraph and data request
        const dynamicDataWithIntro = {
          ...dynamicData,
          selectedIntroParagraph: this.formData.selectedIntroParagraphObject,
          introParagraphId: this.formData.selectedIntroParagraph,
          selectedDataRequest: this.formData.selectedDataRequestObject
        };
        
        const emailContent = await generateEmailTemplate('Property Contact Request', userInfo, dynamicDataWithIntro, accessToken);
        
        // Set the email content subject for metrics - use proper reply subject if replying, otherwise new template subject
        if (this.formData.replyToSelectedEmail && this.emailData && this.emailData.subject) {
          // Create proper reply subject with RE: prefix
          this.emailContentSubject = this.createReplySubject(this.emailData.subject);
        } else {
          this.emailContentSubject = emailContent.subject;
        }
        
        // Check if user wants to reply to selected email and we have an email selected
        if (this.formData.replyToSelectedEmail && this.emailData && this.emailData.subject && window.Office?.context?.mailbox?.item) {
          // Reply to the selected email
          this.createReplyToSelectedEmail(emailContent);
        } else if (window.Office?.context?.mailbox?.displayNewMessageForm) {
          // Create new email with template + signature combined
          const success = await this.createEmailWithTemplateAndSignature(emailContent);
          
          this.$emit('showNotification', {
            type: 'success',
            message: success ? 'Email created with template and signature.' : 'Email created successfully.'
          });
        } else {
          // Fallback to mailto
          const plainTextBody = emailContent.htmlBody.replace(/<[^>]*>/g, '');
          const ccEmail = this.getClientContactEmail();
          const mailtoUrl = ccEmail 
            ? `mailto:${this.formData.propertyContactEmail}?cc=${ccEmail}&subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(plainTextBody)}`
            : `mailto:${this.formData.propertyContactEmail}?subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(plainTextBody)}`;
          
          window.location.href = mailtoUrl;
          
          this.$emit('showNotification', {
            type: 'info',
            message: 'Email client opened with template content.'
          });
        }

        // Mark email as complete IMMEDIATELY after opening the form (Performance Optimization)
        try {
          markEmailAsCompleteNew(
            'completeNotification',
            'Property contact request email created successfully.',
            undefined,
            true
          );
        } catch (markError) {
           console.error("Failed to mark email as complete", markError);
        }

      } catch (error) {
        console.warn('Graph enhancement failed, using basic template:', error);
        // Fallback to basic template without Graph enhancement
        try {
          // Prepare dynamic data with selected intro paragraph
          const dynamicDataWithIntro = {
            ...dynamicData,
            selectedIntroParagraph: this.formData.selectedIntroParagraphObject,
            introParagraphId: this.formData.selectedIntroParagraph
          };
          
          const emailContent = await generateEmailTemplate('appraiser-introduction', userInfo, dynamicDataWithIntro);
          
          // Set the email content subject for metrics - use proper reply subject if replying, otherwise fallback subject
          if (this.formData.replyToSelectedEmail && this.emailData && this.emailData.subject) {
            // Create proper reply subject with RE: prefix
            this.emailContentSubject = this.createReplySubject(this.emailData.subject);
          } else {
            this.emailContentSubject = emailContent.subject;
          }
          
          // Check if user wants to reply to selected email and we have an email selected
          if (this.formData.replyToSelectedEmail && this.emailData && this.emailData.subject && window.Office?.context?.mailbox?.item) {
            this.createReplyToSelectedEmail(emailContent);
          } else if (window.Office?.context?.mailbox?.displayNewMessageForm) {
            // Simple fallback approach with signature
            const success = await this.createEmailWithTemplateAndSignature(emailContent);
            
            this.$emit('showNotification', {
              type: 'success',
              message: success ? 'Email created with basic template and signature.' : 'Email created with basic template.'
            });
          } else {
            const plainTextBody = emailContent.htmlBody.replace(/<[^>]*>/g, '');
            const ccEmail = this.getClientContactEmail();
            const mailtoUrl = ccEmail 
              ? `mailto:${this.formData.propertyContactEmail}?cc=${ccEmail}&subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(plainTextBody)}`
              : `mailto:${this.formData.propertyContactEmail}?subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(plainTextBody)}`;
            
            window.location.href = mailtoUrl;
            
            this.$emit('showNotification', {
              type: 'info',
              message: 'Email client opened with basic template content.'
            });
          }
        
          // Mark email as complete IMMEDIATELY after opening the form (Performance Optimization) - Fallback path
          try {
            markEmailAsCompleteNew(
              'completeNotification',
              'Property contact request email created successfully.',
              undefined,
              true
            );
          } catch (markError) {
             console.error("Failed to mark email as complete", markError);
          }

        } catch (fallbackError) {
          finalStatus = 'Error';
          errorMessage = fallbackError.message;
          console.error('Both Graph and basic template generation failed:', fallbackError);
          this.$emit('showNotification', {
            type: 'error',
            message: 'Failed to generate email template: ' + fallbackError.message
          });
        }
      } finally {
        const endTime = performance.now();
        const manualProcessingTime = Math.round(endTime - this.processingStartTime);

        // Create explicit payload with processingTime
        const explicitPayload = {
          entityId: metricsPayload.entityId,
          fileId: metricsPayload.fileId,
          actionTaken: metricsPayload.actionTaken,
          status: finalStatus,
          subjectLine: this.emailContentSubject,
          processingTime: Math.max(0, manualProcessingTime),
          timestamp: new Date().toISOString(),
          toAddress: this.formData.propertyContactEmail,
        };

        if (errorMessage) {
          explicitPayload.errorMessage = errorMessage;
        }

        // Defensive: Ensure processingTime is always a valid number (never null)
        if (explicitPayload.processingTime == null || isNaN(explicitPayload.processingTime)) {
          explicitPayload.processingTime = 0;
        }

        await this.addMetricsData(explicitPayload);
      }

      // Reset date/time flags when closing so next time modal opens it loads fresh data
      this.userEnteredDate = false;
      this.userEnteredTime = false;

      // Close the modal after successful email creation
      this.$emit('close');
    },

    createReplyToSelectedEmail(emailContent) {
      try {
        // Get the current email item
        const item = Office.context.mailbox.item;
        
        // Get property contact email from form data
        const propertyContactEmail = this.formData.propertyContactEmail;
        
        // Check if property contact email is in the original TO recipients
        const originalToEmails = item.to?.map(recipient => recipient.emailAddress) || [];
        const originalCcEmails = item.cc?.map(recipient => recipient.emailAddress) || [];
        
        const isPropertyContactInTo = originalToEmails.includes(propertyContactEmail);
        const isPropertyContactInCc = originalCcEmails.includes(propertyContactEmail);
        const isPropertyContactAnywhere = isPropertyContactInTo || isPropertyContactInCc;
        
        // Check if additional recipients are in CC
        const missingAdditionalRecipients = this.selectedCCEmails.filter(
          email => !originalCcEmails.includes(email) && !originalToEmails.includes(email)
        );
        
        // Create reply with template content
        item.displayReplyAllForm({
          htmlBody: emailContent.htmlBody
        });
        
        // Build notification message based on missing recipients
        const messages = [];
        if (!isPropertyContactAnywhere && propertyContactEmail) {
          messages.push('Property Contact');
        }
        if (missingAdditionalRecipients.length > 0) {
          messages.push('additional recipients');
        }
        
        if (messages.length > 0) {
          this.$emit('showNotification', {
            type: 'info',
            message: `Reply Created. Please add ${messages.join(' and ')} to recipients.`,
            timeout: 30000
          });
        } else if (isPropertyContactInCc && !isPropertyContactInTo && propertyContactEmail) {
          // Add success banner for properly included recipient
          this.$emit('showNotification', {
            type: 'info',
            message: 'Reply created - Property contact is in CC.'
          });
        } else if (isPropertyContactInTo && propertyContactEmail) {
          // Add success banner for properly included recipient
          this.$emit('showNotification', {
            type: 'success',
            message: 'Reply created with template content!'
          });
        } else {
          // No property contact email available
          this.$emit('showNotification', {
            type: 'success',
            message: 'Reply created with template content!'
          });
        }
        
      } catch (error) {
        console.error('Failed to create reply:', error);
        
        // Fallback to regular new message if reply fails
        if (window.Office?.context?.mailbox?.displayNewMessageForm) {
          Office.context.mailbox.displayNewMessageForm({
            toRecipients: [{
              displayName: `${this.formData.propertyContactFirstName} ${this.formData.propertyContactLastName}`.trim(),
              emailAddress: this.formData.propertyContactEmail
            }],
            ccRecipients: this.selectedCCEmails.map(email => ({
              emailAddress: email
            })),
            subject: emailContent.subject,
            htmlBody: emailContent.htmlBody
          });
          
          this.$emit('showNotification', {
            type: 'warning',
            message: 'Created new email instead (reply not available)'
          });
        }
      }
    },
    getUserInfo() {
      try {
        return {
          email: Office.context.mailbox.userProfile.emailAddress || 'Unknown',
          name: Office.context.mailbox.userProfile.displayName || 'User'
        };
      } catch (error) {
        return {
          email: 'Unknown',
          name: 'User'
        };
      }
    },
    prepareDynamicData() {
      const propertyAddress = getPropertyAddressOrName(this.selectedAssignment, false) || 'the property';
      const propertyName = getPropertyAddressOrName(this.selectedAssignment, true) || '';
    return {
      // Assignment data with null checks
      fileId: this.selectedAssignment?.code || 'N/A',
      clientName: this.selectedAssignment?.clientName || this.getClientContactName() || 'Client',
      propertyAddress: propertyAddress,
      propertyName: propertyName,
      assignmentId: this.selectedAssignment?.assignmentId || this.selectedAssignment?.id || 'N/A',
      
      // Form data with null checks
      dataRequest: this.formData?.dataRequest || 'N/A',
      selectedDataRequest: this.formData?.selectedDataRequestObject, // Pass the full object for custom data requests
      contactName: `${this.formData?.propertyContactFirstName || ''}` || 'Contact Name Not Available',
      propertyContactEmail: this.formData?.propertyContactEmail || 'email@example.com',
      senderName: this.getUserDetails?.fullName || 'Sender',
      primaryValuer: this.selectedAssignment?.primaryAppraiser || 'PRIMARY VALUER',
      sigEmail: this.getUserDetails?.emailAddress,
      primaryValuerEmail: this.assignmentOverviewData?.valuationAssignmentInformation?.primaryValuer.emailAddress,
      // Inspection scheduling with null checks
      proposedInspectionDate: this.formData?.proposedInspectionDate
        ? moment(this.formData.proposedInspectionDate, 'MM/DD/YYYY').format('dddd, MMMM D, YYYY')
        : '',
      proposedInspectionTime: this.formData?.proposedInspectionTime || '',
      // Additional context with null checks
      workFile: this.selectedAssignment?.workFile || 'N/A',
      currentAction: 'Property Contact Request - Appraiser Introduction',
      companyName: this.assignmentOverviewData?.clientDetails?.clientCompany,
      // Box upload URL from environment (use import.meta.env for Vite)
      boxUploadUrl: process.env.VITE_BOX_UPLOAD_URL,
      timestamp: new Date().toLocaleString()
    };
  },

    isValidEmail(email) {
      // Check if email is a string first
      if (!email || typeof email !== 'string') {
        return false;
      }
      // Email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email.trim());
    },

    // Method to expose form validation state to parent components/config.js
    getFormValidationState() {
      return this.isFormValid;
    },

    async createEmailWithTemplateAndSignature(emailContent) {
      try {
        // Prepare email body with signature for new compose emails
        let finalHtmlBody = emailContent.htmlBody;
        
        // Add signature to new emails (not replies)
        // Check for meaningful signature content (not just empty HTML tags)
        const signatureHtml = this.formData.signature || '';
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = signatureHtml;
        const signatureText = tempDiv.textContent || tempDiv.innerText || '';
        
        if (signatureHtml.trim() && signatureText.trim()) {
          // Save signature to roaming settings when it's being used
          try {
            await this.saveSignatureForFutureUse(signatureHtml);
          } catch (saveError) {
            console.warn('Could not save signature:', saveError.message);
          }
          
          // Use the method that preserves rich text formatting
          const cleanedSignature = this.preserveRichTextFormatting(signatureHtml);
          
          // Add signature with preserved formatting and line breaks
          finalHtmlBody += '<br>' + cleanedSignature;
        } 
       
        const emailData = {
          toRecipients: [{
            displayName: `${this.formData.propertyContactFirstName} ${this.formData.propertyContactLastName}`.trim(),
            emailAddress: this.formData.propertyContactEmail
          }],
          ccRecipients: this.selectedCCEmails.map(email => ({
            emailAddress: email
          })),
          subject: emailContent.subject,
          htmlBody: finalHtmlBody
        };
        // Use Office.js displayNewMessageForm to create a new email
        Office.context.mailbox.displayNewMessageForm(emailData);
        return true; // Success
      } catch (error) {
        console.error('❌ Error creating new email with template:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          emailContent: emailContent,
          formData: this.formData
        });
        return false; // Failed
      }
    }
  }
};
</script>

<style scoped>
.v-col-6 {
    flex: 1 0 45% !important;
}
/* Prevent browser autocomplete dropdowns */
.client-communication-wrapper input:-webkit-autofill,
.client-communication-wrapper input:-webkit-autofill:hover,

.client-communication-wrapper input:-webkit-autofill:focus,
.client-communication-wrapper input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset !important;
  -webkit-text-fill-color: #000 !important;
  transition: background-color 5000s ease-in-out 0s;
}

/* Disable autocomplete dropdown suggestions */
.client-communication-wrapper input[autocomplete="new-password"]::-webkit-contacts-auto-fill-button,
.client-communication-wrapper input[autocomplete="new-password"]::-webkit-credentials-auto-fill-button {
  display: none !important;
}

/* Main Container - match Route to Admin styling */
.client-communication-wrapper {
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
  padding: 0;
  position: relative; /* For loading overlay positioning */
}

/* Form disabled state while loading */
.form-disabled {
  pointer-events: none;
  opacity: 0.6;
  user-select: none;
}

/* Reset any inherited flex properties that might interfere */
.client-communication-wrapper * {
  box-sizing: border-box;
}

.client-communication-wrapper .form-field {
  display: block !important;
  width: 100% !important;
}

/* Selected Section - Based on existing patterns */
.selected-info {
  margin-bottom: 16px;
}

.value {
  color: #333;
  margin-right: 24px;
  font-weight: 400;
}

/* Form Fields - Match Route to Admin styling */
.form-field, .field-group {
  margin-bottom: 16px;
  width: 100%;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 16px;
  color: #000;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
}

.template-selection {
  margin-bottom: 16px;
}

/* Selected item display styling - grayed out appearance */
.selected-item-display {
  background-color: #f5f5f5 !important;
  color: #999 !important;
  font-style: italic !important;
  pointer-events: none !important;
}

.selected-item-display:hover {
  background-color: #f5f5f5 !important;
}

/* No data available styling */
.no-data-item {
  color: #666 !important;
  text-align: center !important;
  pointer-events: none !important;
}

.no-data-item:hover {
  background-color: transparent !important;
}

/* Hide dropdown arrow with multiple approaches */
.no-dropdown-icon :deep(.v-input__append) {
  display: none !important;
}

.no-dropdown-icon :deep(.v-field__append-outer) {
  display: none !important;
}

.no-dropdown-icon :deep(.v-autocomplete .v-input__append) {
  display: none !important;
}

.no-dropdown-icon :deep(.v-select__append) {
  display: none !important;
}

/* Target specific icon classes */
.no-dropdown-icon :deep(.mdi-menu-down) {
  display: none !important;
}

.no-dropdown-icon :deep(.mdi-chevron-down) {
  display: none !important;
}

/* Ensure search icon stays visible */
.no-dropdown-icon :deep(.v-field__append-inner) {
  display: flex !important;
}

.no-dropdown-icon :deep(.v-field__append-inner .mdi-magnify) {
  display: block !important;
}

/* Loading Indicator */
.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.loading-text {
  font-size: 14px;
  color: #666;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
}

/* Field Error Styles */
.field-error :deep(.v-field) {
  border-color: #f44336 !important;
}

.field-error :deep(.v-field--focused) {
  border-color: #f44336 !important;
}

.error-message {
  color: #f44336;
  font-size: 14px;
  margin-top: 4px;
  font-weight: 400;
}

/* Select Field Styling */
.client-communication-wrapper :deep(.v-select) {
  font-size: 14px;
}

/* Autocomplete Field Styling */
.client-communication-wrapper :deep(.v-autocomplete) {
  font-size: 14px;
}

.client-communication-wrapper :deep(.v-autocomplete .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px;
  line-height: 48px;
  text-align: left;
}

.client-communication-wrapper :deep(.v-select .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px;
  line-height: 48px;
  text-align: left;
}

.client-communication-wrapper :deep(.v-select .v-field__input::placeholder) {
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
}

/* Direct targeting of Vuetify select field components */
.client-communication-wrapper :deep(.v-select .v-field) {
  height: 48px;
  display: flex;
  align-items: center;
}

.client-communication-wrapper :deep(.v-select .v-field__field) {
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0;
}

.client-communication-wrapper :deep(.v-select .v-field-label) {
  line-height: 1;
  top: 50%;
  transform: translateY(-50%);
}

/* Target the actual select input area */
.client-communication-wrapper :deep(.v-select .v-select__selection-text) {
  line-height: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  text-align: left;
}

/* Ensure placeholder appears centered */
.client-communication-wrapper :deep(.v-select .v-select__selection) {
  height: 48px;
  display: flex;
  align-items: center;
}

/* Text Field Styling */
.client-communication-wrapper :deep(.v-text-field) {
  font-size: 14px;
}

.client-communication-wrapper :deep(.v-text-field .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  padding: 0 16px;
}

.client-communication-wrapper :deep(.v-text-field .v-field__input::placeholder) {
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 400;
  height: 48px;
  text-align: left;
}

/* Textarea Styling - Match Route to Admin */
.client-communication-wrapper :deep(.v-textarea) {
  font-size: 14px;
}

.client-communication-wrapper :deep(.v-textarea .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
}

.client-communication-wrapper :deep(.v-textarea .v-field__input::placeholder) {
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 400;
}

/* Consistent Border Styling for All Form Fields */
.client-communication-wrapper :deep(.v-field) {
  border: 1px solid #5A5A5A !important;
  border-radius: 4px !important;
}

.client-communication-wrapper :deep(.v-field--focused) {
  border: 1px solid #5A5A5A !important;
  box-shadow: none !important;
}

.client-communication-wrapper :deep(.v-field--error) {
  border: 1px solid #f44336 !important;
}

.client-communication-wrapper :deep(.v-field__outline) {
  display: none !important;
}

/* Date Component Border */
.client-communication-wrapper :deep(.date-component .floating-input-container input) {
  border: 1px solid #5A5A5A !important;
  border-radius: 4px !important;
}

.client-communication-wrapper :deep(.date-component .floating-input-container input:focus) {
  border: 1px solid #5A5A5A !important;
  box-shadow: none !important;
}

/* Time Picker Component Border */
.client-communication-wrapper :deep(.time-picker-component .floating-input-container input) {
  border: 1px solid #5A5A5A !important;
  border-radius: 4px !important;
}

.client-communication-wrapper :deep(.time-picker-component .floating-input-container input:focus) {
  border: 1px solid #5A5A5A !important;
  box-shadow: none !important;
}

/* Autocomplete Border */
.client-communication-wrapper :deep(.v-autocomplete .v-field) {
  border: 1px solid #5A5A5A !important;
  border-radius: 4px !important;
}

/* Property Contact Fields - Reduce gap between First Name and Last Name */
.client-communication-wrapper .form-field .v-row {
  margin: 0 !important;
  gap: 8px !important;
}

.client-communication-wrapper .form-field .v-col {
  padding: 0 !important;
}

/* Client Contact Section */
.client-contact {
  padding: 0;
  background-color: transparent;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
}

.contact-name {
  font-weight: 500;
  color: #1A1A1A;
  font-size: 14px;
  flex: 0 0 auto;
  white-space: nowrap;
  margin-right: 8px;
}

.contact-email {
  font-size: 14px;
  color: #1A1A1A;
  font-weight: 500;
  text-align: right;
  min-width: 0;
}

/* Responsive design for mobile devices */
@media (max-width: 320px) {
  .client-contact {
    gap: 4px;
  }
  
  .contact-name {
    margin-right: 4px;
  }
  
  .contact-email {
    text-align: left;
    flex: 1 1 100%;
  }
}

/* Section Header */
.section-header {
  font-size: 16px;
  font-weight: 400;
  color: #000;
  margin-bottom: 12px;
  display: block;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
}

/* Date and Time Fields */
/* Date and Time Fields - Figma Design Layout */
.client-communication-wrapper .date-time-container {
  display: flex !important;
  align-items: flex-end !important;
  gap: 12px !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  margin-bottom: 4px;
  margin-top: 12px !important;
  overflow: visible !important; /* Allow floating label to render above input */
}

.client-communication-wrapper .date-wrapper {
  flex: 1 !important;
  position: relative !important;
  max-width: 45% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.client-communication-wrapper .at-label-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding-bottom: 32px !important;
  min-width: 24px !important;
  max-width: 40px !important;
  flex-shrink: 0 !important;
}

.client-communication-wrapper .time-wrapper {
  flex: 1 !important;
  position: relative !important;
  max-width: 45% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.time-input-row {
  display: flex !important;
  align-items: flex-end !important;
  gap: 8px !important;
  width: 100% !important;
  height: 100% !important;
}

.time-label-inline {
  font-size: 16px;
  font-weight: 400;
  color: #000;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  text-align: center !important;
  margin: 0 !important;
  padding: 0 !important;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
  line-height: 1.2;
  display: block !important;
}

.time-label {
  font-size: 14px;
  font-weight: 500;
  color: #000;
  margin-bottom: 8px;
  display: block;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
}

.time-field-input {
    position: relative;
    top: -8px !important;
    flex: 1 !important;
    min-width: 0 !important;
    height: 52px !important;
}

.client-communication-wrapper :deep(.time-field-input .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
}

.client-communication-wrapper :deep(.time-field-input .v-field__input::placeholder) {
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 400;
}

/* DateComponent styling override */
.client-communication-wrapper .date-wrapper :deep(.date-component) {
  width: 100% !important;
  display: block !important;
}

.client-communication-wrapper .date-wrapper :deep(.date-input) {
  width: 100% !important;
  padding: 8px !important;
  border: 1px solid #5A5A5A !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  background: transparent !important;
  transition: border-color 0.3s ease !important;
  box-sizing: border-box !important;
  cursor: pointer !important;
  height: 48px !important;
  text-align: left !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  padding-left: 12px !important;
}

.client-communication-wrapper .date-wrapper :deep(.floating-label) {
  position: absolute !important;
  left: 8px !important;
  top: 12px !important;
  font-size: 14px !important;
  color: #000000 !important;
  transition: all 0.3s ease !important;
  pointer-events: none !important;
  background: white !important;
  padding: 0 4px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  margin: 0 !important;
  z-index: 2 !important;
}

/* Date floating label when value is selected - match Time exactly */
.client-communication-wrapper .date-wrapper :deep(.floating-input-container .floating-label.float) {
  top: -12px !important;
  font-size: 14px !important;
  color: #000 !important;
  transform: none !important;
  background: #FFFFFF !important;
  padding: 0 4px !important;
  border-radius: 0px !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  line-height: normal !important;
  left: 8px !important;
  position: absolute !important;
  z-index: 10 !important;
}

/* Time floating label base state to match date */
.client-communication-wrapper .time-wrapper :deep(.floating-label) {
  font-size: 14px !important;
  color: #000 !important;
  left: 8px !important;
  top: 12px !important;
  transform: none !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  background: white !important;
  padding: 0 4px !important;
  margin: 0 !important;
  pointer-events: none !important;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1) !important;
  z-index: 2 !important;
  position: absolute !important;
  font-family: 'Segoe UI Web (West European)', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif !important;
}

/* Time floating label when value is selected - prevent cutting */
.client-communication-wrapper .time-wrapper :deep(.floating-input-container .floating-label.float) {
  top: -12px !important;
  transform: none !important;
  font-size: 14px !important;
  background: #FFFFFF !important;
  padding: 2px 6px !important;
  border-radius: 4px !important;
  letter-spacing: 0.25px !important;
  box-shadow: none !important;
  line-height: 1.2 !important;
  left: 8px !important;
  position: absolute !important;
  z-index: 10 !important;
  font-family: 'Segoe UI Web (West European)', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif !important;
  color: #000 !important;
}

/* Ensure containers have enough top margin for floating labels */
.client-communication-wrapper .date-wrapper :deep(.floating-input-container) {
  overflow: visible !important;
  margin-top: 8px !important;
}

.client-communication-wrapper .time-wrapper :deep(.floating-input-container) {
  overflow: visible !important;
  margin-top: 8px !important;
}

/* Ensure no Vuetify overrides break the layout */
.client-communication-wrapper .date-time-container > * {
  flex-shrink: 0 !important;
}

.client-communication-wrapper .time-wrapper .v-text-field {
  width: 100% !important;
}

/* Error handling for select fields */
.client-communication-wrapper :deep(.v-select.v-input--error .v-field) {
  border-color: #f44336 !important;
}

.client-communication-wrapper :deep(.v-select.v-input--error .v-field__outline) {
  color: #f44336 !important;
}

.client-communication-wrapper :deep(.v-select.v-input--error .v-field__outline__start),
.client-communication-wrapper :deep(.v-select.v-input--error .v-field__outline__notch),
.client-communication-wrapper :deep(.v-select.v-input--error .v-field__outline__end) {
  border-color: #f44336 !important;
}

/* Error handling for text fields */
.client-communication-wrapper :deep(.v-text-field.v-input--error .v-field) {
  border-color: #f44336 !important;
}

.client-communication-wrapper :deep(.v-text-field.v-input--error .v-field__outline) {
  color: #f44336 !important;
}

.client-communication-wrapper :deep(.v-text-field.v-input--error .v-field__outline__start),
.client-communication-wrapper :deep(.v-text-field.v-input--error .v-field__outline__notch),
.client-communication-wrapper :deep(.v-text-field.v-input--error .v-field__outline__end) {
  border-color: #f44336 !important;
}

/* Force flex layout at all screen sizes above 351px */
@media (min-width: 351px) {
  .client-communication-wrapper .date-time-container {
    display: -webkit-inline-box !important;
    flex-direction: row !important;
    align-items: flex-end !important;
    gap: 8px !important;
  }
  
  .client-communication-wrapper .date-wrapper {
    flex: 0 0 54% !important;
    min-width: 0 !important;
  }
  
  .client-communication-wrapper .time-wrapper {
    flex: 0 0 43% !important;
    min-width: 0 !important;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .date-wrapper {
    min-width: 180px !important;
  }
  
  .time-wrapper {
    flex: 0 0 130px !important;
    min-width: 100px !important;
  }
}

@media (max-width: 480px) {
  .client-communication-wrapper {
    font-size: 14px;
  }
  
  .field-label {
    font-size: 16px;
  }
  
  .form-field {
    margin-bottom: 12px;
  }
  
  .date-time-row {
    gap: 12px;
  }

  .client-communication-wrapper .date-wrapper :deep(.floating-label) {
    font-size: 14px !important;
    min-width: 70px !important;
    max-width: 100px !important;
    left: 8px !important;
  }
}

@media (max-width: 480px) {
  .action-buttons {
    justify-content: center;
  }
  
  .action-buttons :deep(.v-btn) {
    min-width: 120px;
    font-size: 14px;
  }
}

/* Handle clumsy layout between 321px - 350px */
@media (max-width: 350px) {
  .client-communication-wrapper .date-time-container {
    flex-direction: column !important;
    gap: 12px !important;
    align-items: stretch !important;
  }
  
  .client-communication-wrapper .date-wrapper {
    flex: none !important;
    min-width: none !important;
    max-width: none !important;
  }
  
  .client-communication-wrapper .time-wrapper {
    flex: none !important;
    min-width: none !important;
    max-width: none !important;
  }
 
  .client-communication-wrapper .at-label-wrapper {
    padding-bottom: 0px !important;
  }
}

@media (max-width: 320px) {
  .client-communication-wrapper .date-time-container {
    flex-direction: column !important;
    gap: 16px !important;
    align-items: stretch !important;
  }
  
  .client-communication-wrapper .date-wrapper {
    min-width: none !important;
    max-width: none !important;
    flex: none !important;
  }
  
  .client-communication-wrapper .time-wrapper {
    flex: none !important;
    min-width: none !important;
    max-width: none !important;
  }
  
  .section-header {
    margin-bottom: 8px;
  }
  
  .time-label {
    font-size: 14px;
  }

  .client-communication-wrapper .date-wrapper :deep(.floating-label) {
    font-size: 14px !important;
    min-width: 60px !important;
    max-width: 80px !important;
    transform: none !important;
    left: 8px !important;
  }
  
  .action-buttons {
    padding-top: 16px;
  }
  
  .action-buttons :deep(.v-btn) {
    width: 100%;
    min-width: auto;
  }

  .client-communication-wrapper .at-label-wrapper {
    padding-bottom: 0px !important;
  }

}

/* Reply to Selected Email Checkbox Styling */
.reply-checkbox-section {
  margin-bottom: 16px;
  padding: 0;
}

.reply-checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  margin-left: -8px;
}

.reply-checkbox {
  margin-left: 0;
  margin-right: 0;
}

.checkbox-main-label {
  margin-bottom: 0 !important;
  flex: 1;
}

.checkbox-description {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  padding: 0;
  margin-left: 0; /* Align description to start at the same position as checkbox */
}

.reply-checkbox {
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.reply-checkbox :deep(.v-input__control) {
  min-height: auto;
}

.reply-checkbox :deep(.v-input__details) {
  display: none;
}

.reply-checkbox :deep(.v-selection-control) {
  align-items: flex-start;
  min-height: auto;
}

.reply-checkbox :deep(.v-selection-control__wrapper) {
  height: 20px;
}

/* Signature field styles */
.signature-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.signature-textarea :deep(.v-field) {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.4;
}

.signature-textarea :deep(.v-field__input) {
  font-family: 'Courier New', monospace;
}

.signature-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid #e0e0e0;
}

.format-buttons {
  border-radius: 4px;
  overflow: hidden;
}

.signature-preview {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-top: 8px;
}

/* Signature Field Styles */
.signature-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.signature-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Signature Field Styles */
.signature-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.signature-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.signature-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.signature-vue-editor {
  font-family: Arial, sans-serif !important;
  min-height: 150px;
  width: 100%;
  border: 1px solid #ddd !important;
  border-radius: 4px;
}

.signature-vue-editor :deep(.ql-editor) {
  font-family: Arial, sans-serif !important;
  font-size: 13px !important;
  line-height: 1.4 !important;
  min-height: 120px;
  padding: 12px !important;
}

.signature-vue-editor :deep(.ql-toolbar) {
  border-bottom: 1px solid #ddd;
  background: #f9f9f9;
  border-radius: 4px 4px 0 0;
}

.signature-vue-editor :deep(.ql-container) {
  border-radius: 0 0 4px 4px;
  font-family: Arial, sans-serif !important;
}

.signature-vue-editor :deep(.ql-editor.ql-blank::before) {
  content: 'Enter your signature here...';
  color: #aaa;
  font-style: normal;
}

.signature-char-count {
  text-align: right;
  font-size: 11px;
  color: #666;
  margin-top: 4px;
  padding-right: 8px;
}

.signature-char-count.error {
  color: #f44336;
}

.preview-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
}

.signature-preview-content {
  font-family: Arial, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: #333;
  white-space: pre-wrap;
  background-color: white;
  padding: 10px;
  border-radius: 3px;
  border: 1px solid #ddd;
}

.signature-preview-content div {
  margin-bottom: 2px;
}

.signature-preview-content div:last-child {
  margin-bottom: 0;
}
</style>
